// src/pages/NotificationsPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { BASE_URL } from "../utils";
import { FaSpinner } from "react-icons/fa";
import { FiPaperclip, FiEye } from "react-icons/fi";
import { toast } from "react-toastify";
import { useMode } from "../contexts/ModeContext";

const PAGE_SIZE = 10; // per page

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { mode } = useMode();

  // pagination state
  const [page, setPage] = useState(1);

  // sorting state
  const [sortState, setSortState] = useState({ key: "", dir: "asc" });

  // "Done" loading state
  const [loadingIds, setLoadingIds] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const scope = (searchParams.get("scope") || "today").toLowerCase(); // today|upcoming|all
  const query = searchParams.get("q") || "";

  // HTML modal state
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");

  // ---------------- scope/query setters ----------------
  const setScope = (s) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("scope", s);
      return p;
    });
    setPage(1);
  };

  const setQuery = (q) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (q) p.set("q", q);
      else p.delete("q");
      return p;
    });
    setPage(1);
  };

  // ----------------- HTML helpers -----------------
  const stripHtml = (html) => {
    if (!html) return "";
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      return doc.body.textContent || "";
    } catch (e) {
      return String(html).replace(/<[^>]+>/g, "");
    }
  };

  const truncateText = (text, max = 120) => {
    const s = String(text || "").trim();
    if (s.length <= max) return s;
    return `${s.slice(0, max)}...`;
  };

  const isComplexHtml = (html) => {
    if (!html) return false;
    const lowered = ("" + html).toLowerCase();
    const tagCount = (String(html).match(/<\w+/g) || []).length;
    if (tagCount > 10) return true;
    if (lowered.includes("startfragment")) return true;
    if (
      lowered.includes("<div") ||
      lowered.includes("<table") ||
      lowered.includes("<img")
    )
      return true;
    if (String(html).length > 800) return true;
    return false;
  };

  const sanitizeHtmlForView = (html) => {
    if (!html) return "";
    let s = String(html).replace(
      /<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi,
      "",
    );
    s = s.replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    return s;
  };

  const showHtmlContent = (html) => {
    setHtmlModalContent(sanitizeHtmlForView(html || ""));
    setHtmlModalOpen(true);
  };

  const toggleSort = (key) => {
    setSortState((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "asc" };
    });
    setPage(1);
  };

  const sortIndicator = (key) => {
    if (sortState.key !== key) return "â†•";
    return sortState.dir === "asc" ? "â–²" : "â–¼";
  };

  // ----------------- PDF helper -----------------
  const buildPdfHref = (pdf_url) => {
    if (!pdf_url) return null;
    if (pdf_url.startsWith("http://") || pdf_url.startsWith("https://"))
      return pdf_url;
    const path = pdf_url.startsWith("/") ? pdf_url : `/${pdf_url}`;
    return `${BASE_URL}${path}`;
  };

  // ----------------- Data fetching (fetch ALL for scope, then paginate on UI) -----------------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // big limit to avoid missing data
      const url = `${BASE_URL}/api/case/followups?scope=${encodeURIComponent(scope)}&limit=5000&offset=0`;
      const res = await axios.get(url);
      const data = Array.isArray(res.data) ? res.data : [];
      setItems(data);
    } catch (e) {
      console.error("Notifications fetch error:", e);
      toast.error("Failed to load notifications.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ----------------- Local search filter -----------------
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((it) =>
      [
        it.case_id,
        it.petition_court_type ?? it.petition_court ?? "",
        it.petition_processing_file_type ?? it.petition_type ?? "",
        it.note ?? "",
        dayjs(it.follow_up_date).format("MMM D, YYYY, h:mm A"),
        it.label ?? "",
        it.remark ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [items, query]);

  const sorted = useMemo(() => {
    if (!sortState.key) return filtered;
    const dir = sortState.dir === "asc" ? 1 : -1;

    const getSortValue = (item) => {
      switch (sortState.key) {
        case "case_id":
          return String(item.case_id || "").toLowerCase();
        case "court":
          return String(
            item?.petition_court_type ?? item?.petition_court ?? "",
          ).toLowerCase();
        case "follow_up_date":
          return item.follow_up_date
            ? new Date(item.follow_up_date).getTime()
            : 0;
        case "note":
          return stripHtml(item.note || "").toLowerCase();
        case "label":
          return String(item.label || "").toLowerCase();
        case "remark":
          return String(item.remark || "").toLowerCase();
        default:
          return "";
      }
    };

    return [...filtered].sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filtered, sortState]);

  // ----------------- Pagination calculations -----------------
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const endIdx = startIdx + PAGE_SIZE;

  const pageItems = useMemo(
    () => sorted.slice(startIdx, endIdx),
    [sorted, startIdx, endIdx],
  );

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const goPage = (p) => setPage(Math.max(1, Math.min(totalPages, p)));

  const renderPageButtons = () => {
    const max = 5;
    const half = Math.floor(max / 2);
    let s = Math.max(1, currentPage - half);
    let e = Math.min(totalPages, s + max - 1);
    if (e - s + 1 < max) s = Math.max(1, e - max + 1);

    const btns = [];
    for (let i = s; i <= e; i++) {
      btns.push(
        <button
          key={i}
          onClick={() => goPage(i)}
          className={`px-3 py-1 border rounded text-sm ${
            i === currentPage
              ? "bg-blue-600 text-white"
              : "bg-white hover:bg-slate-50"
          }`}
        >
          {i}
        </button>,
      );
    }
    return btns;
  };

  const buildCaseHref = (item) => {
    const id = item?.case_id ?? "";
    const court = item?.petition_court_type ?? item?.petition_court ?? "";
    const type =
      item?.petition_processing_file_type ?? item?.petition_type ?? "";
    if (!court || !type) return `/case-details/${encodeURIComponent(id)}`;
    return `/websurrogate/case-details/${encodeURIComponent(id)}/${encodeURIComponent(court)}/${encodeURIComponent(type)}`;
  };

  const handleCaseClick = (event, href) => {
    const isWeb =
      mode === "webSurrogate" || mode === "websurrogate" || mode === "web";
    if (!isWeb) {
      event.preventDefault();
      window.alert("You are not in WebSurrogate mode. Please switch to open case details.");
      return;
    }
    if (!href) event.preventDefault();
  };

  const markAsDone = async (case_id) => {
    if (!case_id) return;
    setLoadingIds((prev) => [...prev, case_id]);
    try {
      await axios.post(`${BASE_URL}/api/case/mark-read`, { case_id });
      setItems((prev) => prev.filter((f) => f.case_id !== case_id));
      toast.success("Marked as done");
    } catch (err) {
      console.error("Error updating read status", err);
      toast.error("Failed to mark as done.");
    } finally {
      setLoadingIds((prev) => prev.filter((id) => id !== case_id));
    }
  };

  async function copyToClipboard(
    text,
    successMsg = "Copied",
    failMsg = "Copy failed",
  ) {
    const payload = text == null ? "" : String(text);

    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      try {
        await navigator.clipboard.writeText(payload);
        toast.success(successMsg);
        return true;
      } catch (err) {
        console.warn(
          "navigator.clipboard.writeText failed, falling back:",
          err,
        );
      }
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = payload;
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.style.left = "-9999px";
      textarea.style.width = "1px";
      textarea.style.height = "1px";
      textarea.style.opacity = "0";

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (ok) {
        toast.success(successMsg);
        return true;
      } else {
        toast.error(failMsg);
        return false;
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error(failMsg);
      return false;
    }
  }

  return (
    <div className="p-4 text-[var(--fg)]">
      <div className="filter-panel mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="page-kicker">WebSurrogate</div>
            <h2 className="page-title">Notifications</h2>
            <div className="page-subtitle">Follow-ups and reminders</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="ui-select"
              title="Scope"
            >
              <option value="today">Today</option>
              <option value="upcoming">Upcoming</option>
              <option value="all">All</option>
            </select>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="ui-input w-64"
            />
          </div>
        </div>
      </div>

      {/* Scroll wrapper (fix hidden items) */}
      <div className="border border-[var(--border)] rounded bg-[var(--surface)] shadow-sm">
        <div className="max-h-[72vh] overflow-auto">
          {loading && items.length === 0 ? (
            <div className="text-center text-[var(--fg-muted)] py-10">
              Loading...
            </div>
          ) : pageItems.length === 0 ? (
            <div className="p-4 text-sm text-[var(--fg-muted)] border-b bg-[var(--surface-muted)]">
              No notifications.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-[var(--surface-muted)] border-b border-[var(--border)]">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("case_id")}
                      className="inline-flex items-center gap-1"
                    >
                      Case ID <span>{sortIndicator("case_id")}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("court")}
                      className="inline-flex items-center gap-1"
                    >
                      Court <span>{sortIndicator("court")}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("follow_up_date")}
                      className="inline-flex items-center gap-1"
                    >
                      Follow-up Date{" "}
                      <span>{sortIndicator("follow_up_date")}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("note")}
                      className="inline-flex items-center gap-1"
                    >
                      Note <span>{sortIndicator("note")}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("label")}
                      className="inline-flex items-center gap-1"
                    >
                      Label <span>{sortIndicator("label")}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("remark")}
                      className="inline-flex items-center gap-1"
                    >
                      Remark <span>{sortIndicator("remark")}</span>
                    </button>
                  </th>
                  <th className="text-right px-3 py-2 text-xs font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {pageItems.map((item) => {
                  const href = buildCaseHref(item);
                  const court =
                    item?.petition_court_type ?? item?.petition_court ?? "";
                  const petitionType =
                    item?.petition_processing_file_type ??
                    item?.petition_type ??
                    "";
                  const label = (item.label || "").toUpperCase();
                  const labelStyle =
                    label === "HOT"
                      ? "badge-pill badge-rose"
                      : label === "WARM"
                        ? "badge-pill badge-amber"
                        : label === "COLD"
                          ? "badge-pill badge-neutral"
                          : "badge-pill badge-neutral";

                  const rawNote = item.note || "";
                  const complex = isComplexHtml(rawNote);
                  const plain = stripHtml(rawNote).trim();
                  const notePreview = truncateText(plain || "-");

                  const firstNote =
                    Array.isArray(item.notes) && item.notes.length > 0
                      ? item.notes[0]
                      : null;
                  const pdfUrlRaw =
                    item.pdf_url ||
                    item.pdfUrl ||
                    firstNote?.pdf_url ||
                    firstNote?.pdfUrl ||
                    null;
                  const pdfHref = pdfUrlRaw ? buildPdfHref(pdfUrlRaw) : null;

                  return (
                    <tr
                      key={
                        item.note_id || `${item.case_id}-${item.follow_up_date}`
                      }
                      className="align-top"
                    >
                      <td className="px-3 py-2">
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline font-semibold"
                          title={href}
                          onClick={(event) => handleCaseClick(event, href)}
                        >
                          {item.case_id}
                        </a>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs text-[var(--fg)]">
                          {court || "-"}
                        </div>
                        {petitionType && (
                          <div className="text-[11px] text-[var(--fg-muted)]">
                            {petitionType}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {dayjs(item.follow_up_date).format(
                          "MMM D, YYYY, h:mm A",
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {complex && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                showHtmlContent(rawNote);
                              }}
                              title="View full HTML"
                              className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                              type="button"
                            >
                              <FiEye size={15} className="text-blue-600" />
                            </button>
                          )}
                          <span className="text-xs">{notePreview}</span>
                        </div>
                        {pdfHref && (
                          <div className="mt-1">
                            <a
                              href={pdfHref}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-xs text-blue-600 underline"
                            >
                              <FiPaperclip /> View PDF
                            </a>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block text-[10px] px-2 py-[2px] rounded-full font-semibold ${labelStyle}`}
                        >
                          {label || "NA"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {item.remark || "NA"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => markAsDone(item.case_id)}
                          className="text-xs shrink-0 px-2.5 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                          disabled={loadingIds.includes(item.case_id)}
                          type="button"
                        >
                          {loadingIds.includes(item.case_id) ? (
                            <span className="flex items-center gap-1">
                              <FaSpinner className="animate-spin" /> Done
                            </span>
                          ) : (
                            "Done"
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination bar */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[var(--border)] bg-[var(--surface-muted)] flex-wrap">
          <div className="text-sm text-[var(--fg-muted)]">
            {total === 0 ? (
              <>No records</>
            ) : (
              <>
                Showing <strong>{startIdx + 1}</strong>-
                <strong>{Math.min(endIdx, total)}</strong> of{" "}
                <strong>{total}</strong>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={goPrev}
              disabled={currentPage <= 1}
              className="px-3 py-1 border rounded text-sm bg-[var(--surface)] disabled:opacity-50"
            >
              Prev
            </button>

            <div className="flex items-center gap-1">{renderPageButtons()}</div>

            <button
              onClick={goNext}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 border rounded text-sm bg-[var(--surface)] disabled:opacity-50"
            >
              Next
            </button>

            <div className="ml-2 text-sm text-[var(--fg-muted)]">
              Page <strong>{currentPage}</strong> of{" "}
              <strong>{totalPages}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* HTML Viewer Modal */}
      {htmlModalOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-4xl p-4 max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Last Note - Full HTML</h3>
              <div>
                <button
                  onClick={() => setHtmlModalOpen(false)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  Close
                </button>
              </div>
            </div>

            <div
              className="raw-html-preview prose max-w-full text-sm"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtmlForView(htmlModalContent),
              }}
            />

            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={async () => {
                  try {
                    await copyToClipboard(
                      stripHtml(htmlModalContent),
                      "Copied plain text",
                      "Failed to copy plain text",
                    );
                  } catch (err) {
                    console.error("Copy plain text error:", err);
                    toast.error("Failed to copy plain text");
                  }
                }}
                className="px-3 py-1 border rounded text-sm"
              >
                Copy plain text
              </button>

              <button
                onClick={async () => {
                  try {
                    await copyToClipboard(
                      htmlModalContent,
                      "Copied raw HTML",
                      "Failed to copy raw HTML",
                    );
                  } catch (err) {
                    console.error("Copy raw HTML error:", err);
                    toast.error("Failed to copy raw HTML");
                  }
                }}
                className="px-3 py-1 border rounded text-sm"
              >
                Copy raw HTML
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
